import {
  ButtonProps,
  ColorType,
  FilledButton,
} from "@ifgengineering/component-library";
import React from "react";
import styled, { useTheme } from "styled-components";

/** The dark end of a palette name is unreadable on the dark menu surface. */
const DARK_INK: Partial<Record<ColorType, ColorType>> = {
  ERROR800: "ERROR500",
};

export type FloatingMenuItemProps = ButtonProps;

export const FloatingMenuItem: React.FC<FloatingMenuItemProps> = ({
  height = "54px",
  text,
  textSize = "S16",
  icon,
  onClick,
  textColor,
  iconColor,
  ...props
}): JSX.Element => {
  const theme = useTheme();
  const ink = (given: ColorType | undefined, fallback: ColorType): ColorType =>
    given ? DARK_INK[given] ?? given : fallback;

  return (
    <FilledButton
      color="white"
      text={text}
      textSize={textSize}
      textColor={ink(textColor, "SLATE100")}
      icon={icon}
      iconColor={ink(iconColor, "SLATE300")}
      iconSize={14}
      onClick={onClick}
      height={height}
      customBackgroundColors={{
        hover: theme.colors.ui.background.inset,
        default: theme.colors.ui.background.raised,
      }}
      borderRadius="none"
      justifyContent="left"
      {...props}
    />
  );
};

export interface FloatingMenuProps {
  collectionOfItems: Array<Array<FloatingMenuItemProps>>;
}

const FloatingMenu: React.FC<FloatingMenuProps> = ({
  collectionOfItems,
}): JSX.Element => (
  <FloatingMenuContainer>
    {collectionOfItems.map((items, groupIdx) => (
      <MenuGroupContainer key={"group-" + groupIdx}>
        {items.map((item, itemIdx) => (
          <FloatingMenuItem
            key={`item-${itemIdx}-group-${groupIdx}`}
            data-testid={item.text}
            {...item}
          />
        ))}
      </MenuGroupContainer>
    ))}
  </FloatingMenuContainer>
);

export default FloatingMenu;

const FloatingMenuContainer = styled.div`
  width: 100%;
  box-shadow: ${({ theme }) => theme.elevation.low};
  border-radius: 0 0 8px 8px;
  overflow: hidden;
`;

const MenuGroupContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: start;
  border-top: 0.5px solid ${({ theme }) => theme.colors.ui.border.subtle};
`;
